<script lang="ts" setup>
import {computed, defineAsyncComponent, ref} from "vue"
import SidebarHeader from "@src/components/views/HomeView/Sidebar/SidebarHeader.vue";
import IconButton from "@src/components/ui/inputs/IconButton.vue";
import SearchInput from "@src/components/ui/inputs/SearchInput.vue";
import SessionService from "@src/services/session"
import SessionList from "@src/components/shared/session/SessionList.vue";
import NoConversation from "@src/components/states/empty-states/NoConversation.vue";
import FadeTransition from "@src/components/ui/transitions/FadeTransition.vue";
import ArchivedButton from "@src/components/views/HomeView/Sidebar/Conversations/ArchivedButton.vue";
import Circle2Lines from "@src/components/states/loading-states/Circle2Lines.vue";
import {permissionsHelper} from "@src/helpers/permissionsHelper";
import useStore from "@src/stores/store";
import {useChatSessionStore} from "@src/stores/chatSession";
import {useChatSessions} from "@src/composables/useChatSessions";

import Swal from "sweetalert2";
import ChatSession from "@src/types/ChatSession";
import {Toast} from "@src/plugins/toast";
import {useWAAPIStore} from "@src/stores/waapi";
import InformationIcon from "@src/components/ui/utils/InformationIcon.vue";

const StartConversationModal = defineAsyncComponent(() => import("@src/components/shared/modals/StartConversationModal/StartConversationModal.vue"));
const FiltersModal = defineAsyncComponent(() => import("@src/components/shared/modals/FIltersModal/FiltersModal.vue"));
const BulkTransferSessionModal = defineAsyncComponent(() => import("@src/components/shared/modals/TransferModal/BulkTransferSessionModal.vue"));
const WaapiCampaign = defineAsyncComponent(() => import("@src/components/shared/modals/WaapiCampaignModal/WaapiCampaign.vue"));

const composeOpen = ref(false);
const filtersOpen = ref(false);

const store = useStore();
const sessionStore = useChatSessionStore()
const {getSessionsByStatus} = useChatSessions()
const activeSessions = getSessionsByStatus('active')
const openArchive = ref(false);
const openTransfer = ref(false);
const search = ref<string>('')
const waapiStore = useWAAPIStore()
const costumerToken = ref<string>('')

const hasActiveFilters = computed(() => {
    return (
        sessionStore.filterTagsSelected.length > 0 ||
        sessionStore.filterKanbanSelected.length > 0
        || sessionStore.filterChannelsSelected.length > 0
        || sessionStore.filterUnread
    )
})

const setToken = (token: string) => {
    costumerToken.value = token
    waapiStore.showCampaignModal = true
}

const sortSessions = (sessions: ChatSession[]) => {
    return [...sessions].sort((a, b) => {
        if (a.is_fixed !== b.is_fixed) {
            return a.is_fixed ? -1 : 1
        }
        const aTime = a.last_message?.created_at ? new Date(a.last_message.created_at).getTime() : 0
        const bTime = b.last_message?.created_at ? new Date(b.last_message.created_at).getTime() : 0
        return bTime - aTime
    })
}

const applyFilters = (sessions: ChatSession[]) => {
    let result = sessions

    result = result.filter(s =>
        Boolean(s.is_archive) === sessionStore.showArchivedSessions
    )

    if (sessionStore.filterUnread) {
        result = result.filter(s => s.has_new_messages);
    }

    if (sessionStore.filterTagsSelected.length > 0) {
        result = result.filter(s =>
            sessionStore.filterTagsSelected.every(tagId =>
                s.tags?.some(t => t.id === tagId)
            )
        )
    }

    if (sessionStore.filterChannelsSelected.length > 0) {
        result = result.filter(s =>
            sessionStore.filterChannelsSelected.some(channelId =>
                s.channel?.id === channelId
            )
        )
    }

    if (sessionStore.filterKanbanSelected.length > 0) {
        result = result.filter(s =>
            sessionStore.filterKanbanSelected.includes(s.kanban_card as number)
        )
    }

    return result
}

const filteredSessions = computed(() => {
    let sessions = applyFilters(activeSessions.value ?? [])

    if (!search.value.toLocaleLowerCase()) {
        return sortSessions(sessions)
    }

    let response: ChatSession[] = []
    sessions.forEach(s => {
        if (String(s.id).includes(search.value.trim())
            || s.costumer?.name?.toLocaleLowerCase().includes(search.value.toLocaleLowerCase())
            || s.costumer?.token.includes(search.value) || s.costumer?.username?.includes(search.value)) {
            response.push(s)
        } else if (s.costumer.contact) {
            if (s.costumer.contact.name?.toLocaleLowerCase().includes(search.value.toLocaleLowerCase())
                || s.costumer.contact.nickname?.toLocaleLowerCase().includes(search.value.toLocaleLowerCase())
                || s.costumer.contact.reference?.toLocaleLowerCase().includes(search.value.toLocaleLowerCase())) {
                response.push(s)
            }
        }
    })
    return sortSessions(response)
});

const archivedLength = computed(() => {
    return activeSessions.value?.filter(s => s.is_archive === true).length
})

const unarchivedLength = computed(() => {
    return activeSessions.value?.filter(s => s.is_archive === false).length
})

const closeComposeModal = () => {
    composeOpen.value = false;
};

const closeFiltersModal = () => {
    filtersOpen.value = false;
};

const openActionsBulk = () => {
    sessionStore.actionSessions = []
    sessionStore.openActionsBulk = !sessionStore.openActionsBulk
}

const canShowPauseButton = computed(() => {

    if (sessionStore.actionSessions.length === 0) {
        return false
    }

    return filteredSessions.value
        .filter(session =>
            sessionStore.actionSessions.includes(session.id)
        )
        .every(session => session.department !== null)

})
const canShowReactivateButton = computed(() => {
    if (sessionStore.actionSessions.length === 0) {
        return false
    }

    return filteredSessions.value
        .filter(session =>
            sessionStore.actionSessions.includes(session.id)
        )
        .every(session => session.channel.type === 'waapi' && session.is_expired === true)
})

const bulkPause = async () => {

    if (sessionStore.actionSessions.length === 0) {
        return
    }

    const result = await Swal.fire({
        title: 'Pausar Atendimentos',
        text: 'Você tem certeza que deseja pausar o atendimentos?',
        icon: 'warning',

        showCancelButton: true,
        confirmButtonText: 'Pausar',
        cancelButtonText: 'Cancelar',

        confirmButtonColor: '#e36464',
        cancelButtonColor: '#6b7280',

        reverseButtons: true
    })

    if (result.isConfirmed) {

        const response = await SessionService.bulkPauseSessions(
            sessionStore.actionSessions
        )

        sessionStore.actionSessions = []
        sessionStore.openActionsBulk = !sessionStore.openActionsBulk
    }
}

const bulkTransfer = async () => {
    openTransfer.value = true
}

function handleCloseTransfer() {
    openTransfer.value = false;
}

const bulkReactivate = async () => {
    let sessions = filteredSessions.value.filter(session => sessionStore.actionSessions.includes(session.id))
    if (sessions.length === 0) {
        return
    }
    let channelId = sessions[0].channel.id
    if (sessions.some(session => session.channel.id !== channelId)) {
        Toast.fire("Para reativação em massa, todas as sessões devem pertencer ao mesmo canal.", "", "warning")
        return
    }
    waapiStore.bulk = true
    waapiStore.sessions = sessions
    waapiStore.showCampaignModal = true
}

const bulkFinish = async () => {

    if (sessionStore.actionSessions.length === 0) {
        return
    }

    const result = await Swal.fire({
        title: 'Finalizar Atendimentos',
        text: 'Você tem certeza que deseja finalizar esses atendimentos?',
        icon: 'warning',

        showCancelButton: true,
        confirmButtonText: 'Finalizar',
        cancelButtonText: 'Cancelar',

        confirmButtonColor: '#e36464',
        cancelButtonColor: '#6b7280',

        reverseButtons: true
    })

    if (result.isConfirmed) {

        const response = await SessionService.bulkFinishSessions(
            sessionStore.actionSessions
        )

        sessionStore.actionSessions = []
        sessionStore.openActionsBulk = !sessionStore.openActionsBulk
    }
}

const bulkArchive = async (status: boolean) => {

    if (sessionStore.actionSessions.length === 0) {
        return
    }

    const result = await Swal.fire({
        title: sessionStore.showArchivedSessions ? 'Desarquivar Atendimentos' : 'Arquivar Atendimentos',
        text: 'Você tem certeza que deseja ' + (sessionStore.showArchivedSessions ? 'desarquivar' : ' arquivar ') + ' esses atendimentos?',
        icon: 'warning',

        showCancelButton: true,
        confirmButtonText: sessionStore.showArchivedSessions ? 'Desarquivar' : 'Arquivar',
        cancelButtonText: 'Cancelar',

        confirmButtonColor: '#FE9A00',
        cancelButtonColor: '#6b7280',

        reverseButtons: true
    })

    if (result.isConfirmed) {
        await SessionService.bulkArchiveSessions(
            sessionStore.actionSessions, status
        )
        sessionStore.clearSession()
        Toast.fire(sessionStore.actionSessions.length + (sessionStore.showArchivedSessions ? " sessões desarquivadas com sucesso!" : " sessões arquivadas com sucesso!"), "", "success")
        sessionStore.actionSessions = []
        sessionStore.openActionsBulk = !sessionStore.openActionsBulk
    }
}

</script>

<template>
    <div>
        <SidebarHeader>
            <!--title-->
            <template v-slot:title>
              Ativos
              <InformationIcon title="Ativos" text="Acompanhe seus atendimentos em andamento." class="mb-2" ></InformationIcon>
            </template>

            <!--side actions-->
            <template v-slot:actions>
                <div class="flex items-center">
                    <IconButton v-if="permissionsHelper.has('start_conversation')"
                                aria-label="Iniciar atendimento"
                                class="ic-btn-ghost-primary w-7 h-7 cursor-pointer"
                                title="Iniciar atendimento"
                    >
                        <svg fill="#6A7282" height="22px" viewBox="0 -960 960 960"
                             width="22px" xmlns="http://www.w3.org/2000/svg"
                             @click="composeOpen = true; sessionStore.clearSession()">
                            <path
                                d="M440-400h80v-120h120v-80H520v-120h-80v120H320v80h120v120ZM80-80v-720q0-33 23.5-56.5T160-880h640q33 0 56.5 23.5T880-800v480q0 33-23.5 56.5T800-240H240L80-80Zm126-240h594v-480H160v525l46-45Zm-46 0v-480 480Z"/>
                        </svg>
                    </IconButton>

                    <div>
                        <IconButton
                            aria-label="filter"
                            class="ic-btn-ghost-primary w-7 h-7 relative inline-block ml-2 cursor-pointer"
                            title="Filtrar atendimentos"
                            @click="filtersOpen = true"
                        >
                            <svg :fill="hasActiveFilters ? '#7C8CF5' : '#6A7282'" class="absolute top-[6px] left-[3px]"
                                 height="24px"
                                 viewBox="0 -960 960 960" width="24px" xmlns="http://www.w3.org/2000/svg">
                                <path
                                    d="M440-160q-17 0-28.5-11.5T400-200v-240L168-736q-15-20-4.5-42t36.5-22h560q26 0 36.5 22t-4.5 42L560-440v240q0 17-11.5 28.5T520-160h-80Z"/>
                            </svg>
                            <svg v-if="hasActiveFilters" class="absolute top-[20px] left-[17px]"
                                 fill="#6A7282" height="18px" viewBox="0 -960 960 960"
                                 width="18px" xmlns="http://www.w3.org/2000/svg">
                                <path
                                    d="M508.5-291.5Q520-303 520-320t-11.5-28.5Q497-360 480-360t-28.5 11.5Q440-337 440-320t11.5 28.5Q463-280 480-280t28.5-11.5ZM440-440h80v-240h-80v240Zm40 360q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Z"/>
                            </svg>
                        </IconButton>
                    </div>

                    <IconButton
                        :class="[ 'cursor-pointer ic-btn-ghost-primary w-7 h-7',
                                { 'bg-gray-200 dark:bg-gray-600': sessionStore.openActionsBulk }
                        ]"
                        aria-label="filter"
                        title="Selecionar conversas"
                        @click="openActionsBulk"
                    >
                        <svg class="rotate-90" fill="#6A7282" height="22px" viewBox="0 -960 960 960"
                             width="22px" xmlns="http://www.w3.org/2000/svg">
                            <path
                                d="M240-400q-33 0-56.5-23.5T160-480q0-33 23.5-56.5T240-560q33 0 56.5 23.5T320-480q0 33-23.5 56.5T240-400Zm240 0q-33 0-56.5-23.5T400-480q0-33 23.5-56.5T480-560q33 0 56.5 23.5T560-480q0 33-23.5 56.5T480-400Zm240 0q-33 0-56.5-23.5T640-480q0-33 23.5-56.5T720-560q33 0 56.5 23.5T800-480q0 33-23.5 56.5T720-400Z"/>
                        </svg>
                    </IconButton>
                </div>
            </template>
        </SidebarHeader>

        <!--search bar-->
        <div class="px-5 xs:pb-6 md:pb-5">
            <SearchInput
                :value="search"
                @value-changed="value => {search = value}"
            />
        </div>

        <!--quantidade sessoes-->
        <div v-if="filteredSessions.length > 0" class="flex w-full text-center mb-4">
            <h1 class="w-full heading-2 text-gray-400">{{ filteredSessions.length }}
                {{ filteredSessions.length === 1 ? 'sessão' : 'sessões' }}
                {{
                    sessionStore.showArchivedSessions ? (filteredSessions.length === 1 ? 'arquivada' : 'arquivadas') : (filteredSessions.length === 1 ? 'ativa' : 'ativas')
                }}</h1>
        </div>
        <div>
            <ArchivedButton :archived="archivedLength" :unarchived="unarchivedLength"/>
        </div>

        <!--select session actions-->
        <div v-if="sessionStore.openActionsBulk" class="mb-3 ml-5">
            <div v-if="sessionStore.actionSessions.length > 0">
                <div class="flex items-center">
                    <button
                        class="flex justify-center rounded-md py-3 w-full mr-2 bg-red-400 hover:bg-red-500 text-gray-100 text-sm font-semibold cursor-pointer items-center"
                        @click="bulkFinish()">
                        Finalizar
                        <svg class="ml-3" fill="#e3e3e3" height="24px" viewBox="0 -960 960 960"
                             width="24px" xmlns="http://www.w3.org/2000/svg">
                            <path
                                d="m336-280-56-56 144-144-144-143 56-56 144 144 143-144 56 56-144 143 144 144-56 56-143-144-144 144Z"/>
                        </svg>
                    </button>
                    <button
                        class="flex justify-center rounded-md py-3 w-full bg-brand-400 hover:bg-brand-500 text-gray-100 font-semibold text-sm items-center cursor-pointer"
                        @click="bulkTransfer()">
                        <span>Transferir</span>
                        <svg class="ml-2" fill="#e3e3e3" height="20px" viewBox="0 -960 960 960"
                             width="20px" xmlns="http://www.w3.org/2000/svg">
                            <path
                                d="M280-160 80-360l200-200 56 57-103 103h287v80H233l103 103-56 57Zm400-240-56-57 103-103H440v-80h287L624-743l56-57 200 200-200 200Z"/>
                        </svg>
                    </button>
                </div>
                <div class="flex items-center mt-2">
                    <button
                        v-if="!sessionStore.showArchivedSessions"
                        class="flex justify-center rounded-md py-3 w-full bg-amber-500 hover:bg-amber-600 text-gray-100 font-semibold cursor-pointer text-sm items-center max-w-[50%]"
                        @click="bulkArchive(true)">
                        Arquivar
                        <svg class="ml-3" fill="#e3e3e3" height="20px" viewBox="0 -960 960 960"
                             width="20px" xmlns="http://www.w3.org/2000/svg">
                            <path
                                d="m480-240 160-160-56-56-64 64v-168h-80v168l-64-64-56 56 160 160ZM200-640v440h560v-440H200Zm0 520q-33 0-56.5-23.5T120-200v-499q0-14 4.5-27t13.5-24l50-61q11-14 27.5-21.5T250-840h460q18 0 34.5 7.5T772-811l50 61q9 11 13.5 24t4.5 27v499q0 33-23.5 56.5T760-120H200Zm16-600h528l-34-40H250l-34 40Zm264 300Z"/>
                        </svg>
                    </button>
                    <button
                        v-else
                        class="flex justify-center rounded-md p-3 w-full bg-amber-500 hover:bg-amber-600 text-gray-100 font-semibold cursor-pointer text-sm items-center"
                        @click="bulkArchive(false)">
                        Desarquivar
                        <svg class="ml-3" fill="#F3F4F6" height="20px" viewBox="0 -960 960 960"
                             width="24px" xmlns="http://www.w3.org/2000/svg">
                            <path
                                d="M480-560 320-400l56 56 64-64v168h80v-168l64 64 56-56-160-160Zm-280-80v440h560v-440H200Zm0 520q-33 0-56.5-23.5T120-200v-499q0-14 4.5-27t13.5-24l50-61q11-14 27.5-21.5T250-840h460q18 0 34.5 7.5T772-811l50 61q9 11 13.5 24t4.5 27v499q0 33-23.5 56.5T760-120H200Zm16-600h528l-34-40H250l-34 40Zm264 300Z"/>
                        </svg>
                    </button>

                    <button v-if="canShowPauseButton"
                            class="flex justify-center rounded-md py-3 w-full bg-gray-500 hover:bg-gray-600 text-gray-100 font-semibold cursor-pointer text-sm items-center ml-2"
                            @click="bulkPause()">
                        Pausar
                        <svg class="ml-5" fill="#e3e3e3" height="20px" viewBox="0 -960 960 960"
                             width="20px" xmlns="http://www.w3.org/2000/svg">
                            <path
                                d="M360-320h80v-320h-80v320Zm160 0h80v-320h-80v320ZM480-80q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Z"/>
                        </svg>
                    </button>
                    <button v-if="canShowReactivateButton"
                            class="flex justify-center rounded-md py-3 hover:bg-green-700 w-full bg-green-600 text-green-200 font-semibold cursor-pointer text-sm items-center ml-2"
                            @click="bulkReactivate()">
                        Reativar
                        <img alt="official_icon" class="ml-4 w-6 h-6"
                             src="@src/assets/vectors/official_icon_white.svg"
                        >
                    </button>
                </div>
            </div>

            <div class="flex items-center mt-4">
                <label class="ios-checkbox blue">
                    <input
                        :checked="filteredSessions.length > 0 && filteredSessions.every(s => sessionStore.actionSessions.includes(s.id))"
                        type="checkbox"
                        @change="sessionStore.toggleAllActionSessions(filteredSessions.map(s => s.id))"
                    />
                    <div class="checkbox-wrapper">
                        <div class="checkbox-bg"></div>
                        <svg class="checkbox-icon" fill="none" viewBox="0 0 24 24">
                            <path
                                class="check-path"
                                d="M4 12L10 18L20 6"
                                stroke="currentColor"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="3"
                            ></path>
                        </svg>
                    </div>
                </label>
                <p class="pl-3 heading-2 text-gray-500 dark:text-gray-300">Selecionar todas as conversas</p>
            </div>
        </div>
        <!--conversations-->
        <div
            aria-label="conversations"
            class="w-full h-full scroll-smooth"
            role="list"
            style="overflow-x: visible; overflow-y: scroll"
        >
            <Circle2Lines
                v-for="item in 6"
                v-if="store.status === 'loading'"
            />

            <div v-else>
                <ArchivedButton
                    v-if="store.archivedConversations.length > 0"
                    :open="openArchive"
                    @click="openArchive = !openArchive"
                />

                <div
                    v-if="
                    store.status === 'success'
                  "
                >
                    <FadeTransition>
                        <SessionList :sessions="filteredSessions"/>
                    </FadeTransition>
                </div>

                <div v-else>
                    <NoConversation v-if="store.archivedConversations.length === 0"/>
                </div>
            </div>
        </div>

        <!--compose modal-->
        <Teleport v-if="composeOpen" to="#modal-root">
            <StartConversationModal :close-modal="closeComposeModal" :open="composeOpen" @token="setToken"/>
        </Teleport>

        <Teleport v-if="waapiStore.showCampaignModal" to="#modal-root">
            <WaapiCampaign :costumer-token="costumerToken"/>
        </Teleport>

        <!--filters modal-->
        <Teleport v-if="filtersOpen" to="#modal-root">
            <Transition
                enter-active-class="transition-all duration-300 ease-out"
                enter-from-class="opacity-0 translate-y-2"
                enter-to-class="opacity-100 translate-y-0"
                leave-active-class="transition-all duration-200 ease-in"
                leave-from-class="opacity-100 translate-y-0"
                leave-to-class="opacity-0 translate-y-2">
                <FiltersModal :close-modal="closeFiltersModal" :open="filtersOpen"/>
            </Transition>
        </Teleport>

        <Teleport v-if="openTransfer" to="#modal-root">
            <BulkTransferSessionModal
                :close-modal="handleCloseTransfer"
                :open="openTransfer"
            />
        </Teleport>
    </div>
</template>


<style scoped>
.checkbox-container {
    display: flex;
    gap: 20px;
    padding: 20px;
    background: #f8fafc;
    border-radius: 16px;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -2px rgba(0, 0, 0, 0.05);
}

.ios-checkbox {
    --checkbox-size: 22px;
    --checkbox-color: #3b82f6;
    --checkbox-bg: #dbeafe;
    --checkbox-border: #93c5fd;

    position: relative;
    display: inline-block;
    cursor: pointer;
    user-select: none;
    -webkit-tap-highlight-color: transparent;
}

.ios-checkbox input {
    display: none;
}

.checkbox-wrapper {
    position: relative;
    width: var(--checkbox-size);
    height: var(--checkbox-size);
    border-radius: 8px;
    transition: transform 0.2s ease;
}

.checkbox-bg {
    position: absolute;
    inset: 0;
    border-radius: 8px;
    border: 2px solid var(--checkbox-border);
    transition: all 0.2s ease;
}

.checkbox-icon {
    position: absolute;
    inset: 0;
    margin: auto;
    width: 80%;
    height: 80%;
    color: white;
    transform: scale(0);
    transition: all 0.2s ease;
}

.check-path {
    stroke-dasharray: 40;
    stroke-dashoffset: 40;
    transition: stroke-dashoffset 0.3s ease 0.1s;
}

/* Checked State */
.ios-checkbox input:checked + .checkbox-wrapper .checkbox-bg {
    background: var(--checkbox-color);
    border-color: var(--checkbox-color);
}

.ios-checkbox input:checked + .checkbox-wrapper .checkbox-icon {
    transform: scale(1);
}

.ios-checkbox input:checked + .checkbox-wrapper .check-path {
    stroke-dashoffset: 0;
}

/* Hover Effects */
.ios-checkbox:hover .checkbox-wrapper {
    transform: scale(1.05);
}

/* Active Animation */
.ios-checkbox:active .checkbox-wrapper {
    transform: scale(0.95);
}

/* Focus Styles */
.ios-checkbox input:focus + .checkbox-wrapper .checkbox-bg {
    box-shadow: 0 0 0 4px var(--checkbox-bg);
}

/* Color Themes */
.ios-checkbox.blue {
    --checkbox-color: #7C8CF5;
    --checkbox-bg: #dbeafe;
    --checkbox-border: #7C8CF5;
}

/* Animation */
@keyframes bounce {
    0%,
    100% {
        transform: scale(1);
    }
    50% {
        transform: scale(1.1);
    }
}

.ios-checkbox input:checked + .checkbox-wrapper {
    animation: bounce 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

</style>